import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'
import { addToPrestataireItems } from '../prestataires/prestatairesSlice'
import { buildEntiteFacturableValue, getModeFacturationCompany } from '../interventions/interventionsSlice'

export const FactureStatuses = [
  { value: 'en_attente', label: "en attente"},
  { value: 'a_payer', label: "à payer"},
  { value: 'payee', label: "payée"},
  { value: 'annulee', label: "annulée"}
]

export const getEntretienTypeOptions = (bien) => {
  if(!bien || !bien.entretiens_reguliers) {
    return []
  }
  const options = []
  if(bien.entretiens_reguliers && bien.entretiens_reguliers.length > 0) {
    bien.entretiens_reguliers.forEach( entretienType => {
      if(entretienType.is_regulier) {
        options.push({
          label: entretienType.nom,
          value: entretienType.entretien_type_id
        })
      }
    })
  }
  return options
}

const getStatusLabel = (statuses, key) => 
  statuses.find( s => s.value === key )?.label

export const getFactureStatusLabel = (status) => getStatusLabel(FactureStatuses, status)

const URL = 'entretiens'
const getUrl = (bien_id, id) => {
  const url = `biens/${bien_id}/${URL}`
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
  isCreatingNewPrestataire: false
}

const sanitizeEntretienFormData = (data) => {
  const sanitizedData = sanitizeFormData(data)
  delete sanitizedData.bien_id
  sanitizedData.prestataire_id = sanitizedData.prestataire?.id || null
  delete sanitizedData.prestataire
  delete sanitizedData.entretien_type

  // Traiter le champ entite_facturable
  if(sanitizedData.entite_facturable) {
    const [type, modeOrId] = sanitizedData.entite_facturable.split(':')
    sanitizedData.entite_facturable_type = type
    
    if (type === 'Company') {
      // Pour Company, l'ID est toujours 1 et utiliser getModeFacturationCompany pour le mode
      sanitizedData.entite_facturable_id = 1
      sanitizedData.mode_facturation_company = getModeFacturationCompany(sanitizedData.entite_facturable)
    } else {
      // Pour Proprietaire, le deuxième paramètre est l'ID
      sanitizedData.entite_facturable_id = modeOrId
    }
  }
  delete sanitizedData.entite_facturable

  return { entretien: sanitizedData }
}

// Thunks
export const fetchEntretiens = createApiAsyncThunk(
  'entretiens/fetchEntretiens',
  async (params = {}, { api, dispatch, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      let url = URL
      if (params.bien_id) {
        url = `biens/${params.bien_id}/entretiens`
        delete params.bien_id
      }
      const response = await api
        .url(url)
        .query(params)
        .get()
      dispatch(addToPrestataireItems(response.entretiens.map( e => e.prestataire)))
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des entretiens')
    }
  }
)

export const createEntretien = createApiAsyncThunk(
  'entretiens/createEntretien',
  async ({ bien_id, data}, { api, dispatch, rejectWithValue }) => {
    try {
      if(!bien_id) throw new Error("Un entretien doit être attaché à un bien")

      const response = await api
        .url(getUrl(bien_id))
        .post(sanitizeEntretienFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Entretien créé avec succès"}))
      if(response.prestataire_id) {
        dispatch(addToPrestataireItems([response.prestataire]))
      }
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateEntretien = createApiAsyncThunk(
  'entretiens/updateEntretien',
  async ({ bien_id, id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(bien_id, id))
        .put(sanitizeEntretienFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Entretien mis à jour avec succès"}))
      if(response.prestataire_id) {
        dispatch(addToPrestataireItems([response.prestataire]))
      }
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteEntretien = createApiAsyncThunk(
  'entretiens/deleteEntretien',
  async (entretien, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(entretien.bien_id, entretien.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Entretien a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour un entretien vide
export const emptyEntretien = {
  bien_id: null,
  prestataire_id: null,
  entretien_type_id: null,
  date_prestation: '',
  date_prochaine_prestation: '',
  montant: '',
  statut_facture: ''
}

const sanitizesPayload = (action) => {
  const sanitized = Object.fromEntries(
    Object.entries(action.payload).map(([key, value]) =>
      [key, value === null ? (emptyEntretien[key] || "") : value]
    )
  )

  // Reconstituer la valeur entite_facturable pour le formulaire
  if (sanitized.entite_facturable_type) {
    sanitized.entite_facturable = buildEntiteFacturableValue(sanitized)
  }
  
  return sanitized
}

// Slice
const entretiensSlice = createSlice({
  name: 'entretiens',
  initialState,
  reducers: {
    setEmptyCurrentEntretien: (state, action) => {
      const data = {
        ...emptyEntretien,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentEntretien: (state, action) => {
      const sanitizedData = {
        ...emptyEntretien,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentEntretien: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setEntretienInEdition: (state, action) => {
      state.inEdition = {
        ...emptyEntretien,
        ...sanitizesPayload(action)
      }
      state.requestStatus = initialState.requestStatus
    },
    resetEntretienInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
    // Actions pour la gestion de la création du prestataire 
    startNewPrestataireCreation: (state) => {
      state.isCreatingNewPrestataire = true
    },
    finishNewPrestataireCreation: (state, action) => {
      const newPrestataire = {
        id: action.payload.id,
        nom_complet: action.payload.nom_complet || `${action.payload.prenom} ${action.payload.nom}`,
        email: action.payload.email,
        telephone: action.payload.telephone,
      }

      state.inEdition = {
        ...state.inEdition,
        prestataire: newPrestataire
      }

      state.isCreatingNewPrestataire = false
    },
    cancelNewPrestataireCreation: (state) => {
      state.isCreatingNewPrestataire = false
    }
  },
  extraReducers: (builder) => {
    // fetchEntretiens
    builder.addCase(fetchEntretiens.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchEntretiens.fulfilled, (state, action) => {
      state.items = action.payload.entretiens.map( item => ({
        ...item,
        entite_facturable: buildEntiteFacturableValue(item)
      }))
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchEntretiens.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createEntretien
    builder.addCase(createEntretien.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createEntretien.fulfilled, (state, action) => {
      state.items.unshift({
        ...action.payload,
        entite_facturable: buildEntiteFacturableValue(action.payload)
      })
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createEntretien.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateEntretien
    builder.addCase(updateEntretien.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateEntretien.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = {
          ...updated,
          entite_facturable: buildEntiteFacturableValue(updated)
        }
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateEntretien.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Entretien
    builder.addCase(deleteEntretien.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteEntretien.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteEntretien.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const {
  setEmptyCurrentEntretien,
  setCurrentEntretien,
  resetCurrentEntretien,
  setEntretienInEdition,
  resetEntretienInEdition,
  setFilters,
  resetRequestStatus,
  startNewPrestataireCreation,
  finishNewPrestataireCreation,
  cancelNewPrestataireCreation
} = entretiensSlice.actions

export default entretiensSlice.reducer